import {useEffect} from 'react';

import type {MediaController} from '@pexip/media';
import {isRequestPermissionNeeded} from '@pexip/media';
import type {Signal} from '@pexip/signal';
import type {IndexedDevices} from '@pexip/media-control';

import {logger} from '../logger';

export const useMedia = (
    mediaService: MediaController,
    devicesChangedSubscribe: Signal<IndexedDevices>['add'],
) => {
    useEffect(() => {
        // This should only be run for the initial media request so we could get
        // the user media when the permissions are already granted
        mediaService.tryAndGetUserMedia({audio: true, video: true});
        const remove = devicesChangedSubscribe(devices => {
            const requestAudio = devices.size('audioinput') > 0;
            const requestVideo = devices.size('videoinput') > 0;
            if (
                // Avoid double GUM requests
                !mediaService.updatingMedia &&
                mediaService.media?.status &&
                !isRequestPermissionNeeded(mediaService.media?.status) &&
                (requestAudio || requestVideo)
            ) {
                mediaService.getUserMedia({
                    audio: requestAudio,
                    video: requestVideo,
                });
            }
        });

        return () => {
            logger.debug('Media cleanup');
            remove();
            mediaService.media?.release().catch((error: unknown) => {
                logger.error({error}, 'GUM cleanup failed');
            });
        };
    }, [devicesChangedSubscribe, mediaService]);
};
